import React, { useEffect, useState } from "react";
import { Settings, Save, Shield, Database, Bell } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { useToast } from "../../../hooks/useToast";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Switch } from "../../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  getSettings,
  saveSettings,
  updateSettings,
} from "../../../services/apiservices/settings";

const SuperUserSettings = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState({});
  const [data, setData] = useState();

  const fetchSystemSettings = () => {
    getSettings("system_settings", (res) => {
      const settingsData = Array.isArray(res?.data) ? res.data[0] : res?.data;

      if (settingsData) {
        setSettings({
          systemName: settingsData?.systemName || "",
          systemEmail: settingsData?.systemEmail || "",
          maintenanceMode: settingsData?.maintenanceMode || false,
          debugMode: settingsData?.debugMode || false,
          emailNotifications: settingsData?.emailNotifications || false,
          pushNotifications: settingsData?.pushNotifications || false,
          sessionTimeout: settingsData?.sessionTimeout || 0,
          passwordPolicy: settingsData?.passwordPolicy || "basic",
          twoFactorRequired: settingsData?.twoFactorRequired || false,
          auditLogging: settingsData?.auditLogging || false,
          autoBackup: settingsData?.autoBackup || false,
          backupFrequency: settingsData?.backupFrequency || "hourly",
          backupRetention: settingsData?.backupRetention || 0,
          cacheEnabled: settingsData?.cacheEnabled || false,
          compressionEnabled: settingsData?.compressionEnabled || false,
          cdnEnabled: settingsData?.cdnEnabled || false,
        });

        setData(res?.data); // Keep original for save/update reference
      } else {
        setSettings({});
        setData(null);
      }
    });
  };

  useEffect(() => {
    fetchSystemSettings();
  }, []);

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const dataToSave = {
      systemName: settings?.systemName || "",
      systemEmail: settings?.systemEmail || "",
      maintenanceMode: settings?.maintenanceMode || false,
      debugMode: settings?.debugMode || false,
      emailNotifications: settings?.emailNotifications || false,
      pushNotifications: settings?.pushNotifications || false,
      sessionTimeout: parseInt(settings?.sessionTimeout || 0),
      passwordPolicy: settings?.passwordPolicy || "basic",
      twoFactorRequired: settings?.twoFactorRequired || false,
      auditLogging: settings?.auditLogging || false,
      autoBackup: settings?.autoBackup || false,
      backupFrequency: settings?.backupFrequency || "hourly",
      backupRetention: parseInt(settings?.backupRetention || 0),
      cacheEnabled: settings?.cacheEnabled || false,
      compressionEnabled: settings?.compressionEnabled || false,
      cdnEnabled: settings?.cdnEnabled || false,
    };

    if (data) {
      const id = data[0]?.id;
      const details = await updateSettings(dataToSave, "system_settings", id);
      if (details.status === true) {
        toast({
          title: "Settings Update",
          description: "System settings have been updated successfully",
        });
        fetchSystemSettings();
      }
    } else {
      const details = await saveSettings(dataToSave, "system_settings");
      if (details.status === true) {
        toast({
          title: "Settings Save",
          description: "System settings save successfully",
        });
        fetchSystemSettings();
      }
    }
  };


  return (
    <div className="p-24 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-24">
        <div>
          <h1 className="text-2xl font-bold flex items-center text-foreground">
            <Settings className="mr-8 h-6 w-6 text-gray-500" />
            System Settings
          </h1>
          <p className="text-gray-500 mt-4">
            Configure system-wide settings and preferences
          </p>
        </div>
        <Button onClick={handleSave} className="flex items-center">
          <Save className="mr-8 h-4 w-4" />
          Save Changes
        </Button>
      </div>

      <div className="space-y-6">
        {/* General Settings */}
        <Card className="bg-white dark:bg-[#2c3440]">
          <CardHeader className="p-24">
            <CardTitle className="flex items-center">
              <Settings className="mr-8 h-5 w-5" />
              General Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-0 pb-24 ph-24">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="systemName">System Name</Label>
                <Input
                  id="systemName"
                  value={settings.systemName || ""}
                  onChange={(e) =>
                    handleSettingChange("systemName", e.target.value)
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="systemEmail">System Email</Label>
                <Input
                  id="systemEmail"
                  type="email"
                  value={settings.systemEmail || ""}
                  onChange={(e) =>
                    handleSettingChange("systemEmail", e.target.value)
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <Label htmlFor="maintenanceMode">Maintenance Mode</Label>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Put system in maintenance mode
                  </p>
                </div>
                <Switch
                  id="maintenanceMode"
                  checked={settings.maintenanceMode}
                  onCheckedChange={(checked) =>
                    handleSettingChange("maintenanceMode", checked)
                  }
                />
              </div>

              <div className="flex items-center justify-between p-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <Label htmlFor="debugMode">Debug Mode</Label>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Enable debug logging
                  </p>
                </div>
                <Switch
                  id="debugMode"
                  checked={settings.debugMode}
                  onCheckedChange={(checked) =>
                    handleSettingChange("debugMode", checked)
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-[#2c3440]">
          <CardHeader className="p-24">
            <CardTitle className="flex items-center">
              <Bell className="mr-8 h-5 w-5" />
              Notification Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-0 pb-24 ph-24">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <Label>Email Notifications</Label>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Send email notifications
                  </p>
                </div>
                <Switch
                  checked={settings.emailNotifications}
                  onCheckedChange={(checked) =>
                    handleSettingChange("emailNotifications", checked)
                  }
                />
              </div>

              <div className="flex items-center justify-between p-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <Label>Push Notifications</Label>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Send push notifications
                  </p>
                </div>
                <Switch
                  checked={settings.pushNotifications}
                  onCheckedChange={(checked) =>
                    handleSettingChange("pushNotifications", checked)
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-[#2c3440]">
          <CardHeader className="p-24">
            <CardTitle className="flex items-center">
              <Shield className="mr-8 h-5 w-5" />
              Security Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-0 pb-24 ph-24">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sessionTimeout">
                  Session Timeout (minutes)
                </Label>
                <Input
                  id="sessionTimeout"
                  type="number"
                  value={settings.sessionTimeout}
                  onChange={(e) =>
                    handleSettingChange("sessionTimeout", e.target.value)
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="passwordPolicy">Password Policy</Label>
                <Select
                  value={settings.passwordPolicy}
                  onValueChange={(value) =>
                    handleSettingChange("passwordPolicy", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="strong">Strong</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <Label>Two-Factor Authentication</Label>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Require 2FA for all users
                  </p>
                </div>
                <Switch
                  checked={settings.twoFactorRequired}
                  onCheckedChange={(checked) =>
                    handleSettingChange("twoFactorRequired", checked)
                  }
                />
              </div>

              <div className="flex items-center justify-between p-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <Label>Audit Logging</Label>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Log all user actions
                  </p>
                </div>
                <Switch
                  checked={settings.auditLogging}
                  onCheckedChange={(checked) =>
                    handleSettingChange("auditLogging", checked)
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Backup Settings */}
          <Card className="bg-white dark:bg-[#2c3440]">
            <CardHeader className="p-24">
              <CardTitle className="flex items-center">
                <Database className="mr-8 h-5 w-5" />
                Backup Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-0 pb-24 ph-24">
              <div className="flex items-center justify-between p-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <Label>Auto Backup</Label>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Enable automatic backups
                  </p>
                </div>
                <Switch
                  checked={settings.autoBackup}
                  onCheckedChange={(checked) =>
                    handleSettingChange("autoBackup", checked)
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="backupFrequency">Backup Frequency</Label>
                <Select
                  value={settings.backupFrequency}
                  onValueChange={(value) =>
                    handleSettingChange("backupFrequency", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="backupRetention">Retention (days)</Label>
                <Input
                  id="backupRetention"
                  type="number"
                  value={settings.backupRetention}
                  onChange={(e) =>
                    handleSettingChange("backupRetention", e.target.value)
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Performance Settings */}
          <Card className="bg-white dark:bg-[#2c3440]">
            <CardHeader className="p-24">
              <CardTitle className="flex items-center">
                Performance Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-0 pb-24 ph-24">
              <div className="flex items-center justify-between p-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <Label>Cache Enabled</Label>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Enable system caching
                  </p>
                </div>
                <Switch
                  checked={settings.cacheEnabled}
                  onCheckedChange={(checked) =>
                    handleSettingChange("cacheEnabled", checked)
                  }
                />
              </div>

              <div className="flex items-center justify-between p-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <Label>Compression</Label>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Enable response compression
                  </p>
                </div>
                <Switch
                  checked={settings.compressionEnabled}
                  onCheckedChange={(checked) =>
                    handleSettingChange("compressionEnabled", checked)
                  }
                />
              </div>

              <div className="flex items-center justify-between p-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <Label>CDN Enabled</Label>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Use content delivery network
                  </p>
                </div>
                <Switch
                  checked={settings.cdnEnabled}
                  onCheckedChange={(checked) =>
                    handleSettingChange("cdnEnabled", checked)
                  }
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SuperUserSettings;
